import { useState, useEffect } from 'react'
import { Card, Row, Col, Statistic, Table, DatePicker, Space, Button, message } from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { statsApi } from '../services/api'

const { RangePicker } = DatePicker

export default function Reports() {
  const [loading, setLoading] = useState(false)
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(30, 'day'),
    dayjs()
  ])
  const [dailySales, setDailySales] = useState<any[]>([])
  const [topProducts, setTopProducts] = useState<any[]>([])
  const [topCustomers, setTopCustomers] = useState<any[]>([])
  const [overview, setOverview] = useState({
    products: 0, users: 0, orders: 0, sales: 0, activeUsers: 0
  })

  useEffect(() => { loadData() }, [dateRange])

  const getParams = () => ({
    start_date: dateRange[0].format('YYYY-MM-DD'),
    end_date: dateRange[1].format('YYYY-MM-DD')
  })

  const loadData = async () => {
    setLoading(true)
    try {
      const params = getParams()
      const [dashRes, topProRes, topCusRes, dailyRes] = await Promise.all([
        statsApi.getDashboard(params),
        statsApi.getTopProducts({ ...params, limit: 10 }),
        statsApi.getTopCustomers({ ...params, limit: 10 }),
        statsApi.getDailySales(params)
      ])

      if (dashRes.code === 200) {
        setOverview(dashRes.data || {})
      }

      if (topProRes.code === 200) {
        setTopProducts((topProRes.data || []).map((p: any, i: number) => ({
          key: p.id || i,
          rank: p.rank,
          name: p.name || '-',
          sales: p.sales,
          revenue: p.revenue
        })))
      }

      if (topCusRes.code === 200) {
        setTopCustomers((topCusRes.data || []).map((c: any, i: number) => ({
          key: c.user_id || i,
          rank: c.rank,
          phone: c.phone || '-',
          orderCount: c.orderCount,
          totalAmount: c.totalAmount
        })))
      }

      if (dailyRes.code === 200) {
        setDailySales((dailyRes.data || []).map((d: any) => ({
          key: d.date,
          date: d.date,
          amount: d.amount
        })))
      }
    } catch {
      message.error('加载数据失败')
    } finally {
      setLoading(false)
    }
  }

  const totalRevenue = dailySales.reduce((sum, d) => sum + (d.amount || 0), 0)

  const productColumns = [
    { title: '排名', dataIndex: 'rank', key: 'rank', width: 60 },
    { title: '商品名称', dataIndex: 'name', key: 'name' },
    {
      title: '销量',
      dataIndex: 'sales',
      key: 'sales',
      render: (v: number) => v || 0
    },
    {
      title: '销售额',
      dataIndex: 'revenue',
      key: 'revenue',
      render: (v: number) => `¥${Number(v || 0).toFixed(2)}`
    }
  ]

  const customerColumns = [
    { title: '排名', dataIndex: 'rank', key: 'rank', width: 60 },
    { title: '手机号', dataIndex: 'phone', key: 'phone' },
    {
      title: '订单数',
      dataIndex: 'orderCount',
      key: 'orderCount',
      render: (v: number) => v || 0
    },
    {
      title: '消费金额',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render: (v: number) => `¥${Number(v || 0).toFixed(2)}`
    }
  ]

  const salesColumns = [
    { title: '日期', dataIndex: 'date', key: 'date' },
    {
      title: '销售额',
      dataIndex: 'amount',
      key: 'amount',
      render: (v: number) => `¥${Number(v || 0).toFixed(2)}`
    }
  ]

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>数据统计</h2>
        <Space>
          <RangePicker
            value={dateRange}
            onChange={(dates) => {
              if (dates && dates[0] && dates[1]) {
                setDateRange([dates[0]!, dates[1]!])
              }
            }}
            allowClear={false}
          />
          <Button icon={<ReloadOutlined />} onClick={loadData} loading={loading}>刷新</Button>
        </Space>
      </div>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={4}>
          <Card><Statistic title="商品总数" value={overview.products || 0} /></Card>
        </Col>
        <Col span={4}>
          <Card><Statistic title="用户总数" value={overview.users || 0} /></Card>
        </Col>
        <Col span={4}>
          <Card><Statistic title="订单总数" value={overview.orders || 0} /></Card>
        </Col>
        <Col span={4}>
          <Card><Statistic title="销售总额" value={overview.sales || 0} prefix="¥" precision={2} /></Card>
        </Col>
        <Col span={4}>
          <Card><Statistic title="活跃客户" value={overview.activeUsers || 0} /></Card>
        </Col>
        <Col span={4}>
          <Card><Statistic title="区间销售" value={totalRevenue} prefix="¥" precision={2} /></Card>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={24}>
          <Card title="日销售趋势">
            <Table
              dataSource={dailySales}
              columns={salesColumns}
              pagination={{ pageSize: 15, size: 'small' }}
              loading={loading}
              size="small"
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Card title="商品销量排行">
            <Table
              dataSource={topProducts}
              columns={productColumns}
              pagination={{ pageSize: 10, size: 'small' }}
              loading={loading}
              size="small"
            />
          </Card>
        </Col>
        <Col span={12}>
          <Card title="客户消费排行">
            <Table
              dataSource={topCustomers}
              columns={customerColumns}
              pagination={{ pageSize: 10, size: 'small' }}
              loading={loading}
              size="small"
            />
          </Card>
        </Col>
      </Row>
    </div>
  )
}
